import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Play, Loader2, Send, Mic, MicOff, Volume2, VolumeX, Sparkles, Coins, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useSession } from '../context/SessionContext';
import { isFeatureUnlocked, generatePremiumContent } from '../services/iapService';
import { trackEvent, trackPageView } from '../services/analytics';
import CreditsRefillModal from '../components/CreditsRefillModal';
import { 
  getDomainStatus, 
  getGenerationUserId, 
  startBatchGeneration,
  startSequentialGeneration,
  DOMAIN_ORDER 
} from '../services/domainTtsService';
import { hasAudioLocal } from '../utils/localAudioCache';
import { verifyAndGetCachedStory } from '../utils/verifyUserData';
import { setSageCredits as persistSageCredits } from '../utils/sageCredits';
import { PlanetBody } from './LoadingPastStoryReels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Planet configuration - distinct colors but harmonious warm-cool balance
// `lord` = Vedic planetary lord (graha) ruling this domain — shown as small subtitle under domain label
const domains = [
  { id: 1, name: 'Health', domainKey: 'health', planet: 'sun', type:'sun', lord:'Surya', grad:['#FFFBD0','#FFD24A','#E89020','#8A4A08'], color: '#FFD24A', glowColor: 'rgba(255,210,74,0.4)', angle: 15, orbitSpeed: 35, sizeRatio: 1.0 },
  { id: 2, name: 'Marriage', domainKey: 'marriage', planet: 'venus', type:'venus', lord:'Shukra', grad:['#FFE9C2','#FFCE96','#C88A4A','#6B3E1A'], color: '#FFCE96', glowColor: 'rgba(255,206,150,0.35)', angle: 60, orbitSpeed: 30, sizeRatio: 0.95 },
  { id: 3, name: 'Career', domainKey: 'career', planet: 'saturn', type:'saturn', lord:'Shani', grad:['#FFEBC0','#E8C278','#9E7830','#523A10'], color: '#E8C278', glowColor: 'rgba(232,194,120,0.38)', angle: 105, orbitSpeed: 50, sizeRatio: 1.12 },
  { id: 4, name: 'Wealth', domainKey: 'wealth', planet: 'jupiter', type:'jupiter', lord:'Guru', grad:['#F8D8B0','#E8A36E','#A86230','#5A2E10'], color: '#E8A36E', glowColor: 'rgba(232,163,110,0.35)', angle: 150, orbitSpeed: 45, sizeRatio: 1.2 },
  { id: 5, name: 'Family', domainKey: 'family', planet: 'moon', type:'moon', lord:'Chandra', grad:['#F0EAFF','#DCD4F5','#8A80B8','#3A3060'], color: '#DCD4F5', glowColor: 'rgba(220,212,245,0.3)', angle: 195, orbitSpeed: 20, sizeRatio: 0.78 },
  { id: 6, name: 'Education', domainKey: 'education', planet: 'mercury', type:'mercury', lord:'Budh', grad:['#C4E8D8','#8BC9B2','#4A7E6A','#1E3E32'], color: '#8BC9B2', glowColor: 'rgba(139,201,178,0.3)', angle: 240, orbitSpeed: 18, sizeRatio: 0.72 },
  { id: 7, name: 'Spiritual', domainKey: 'spiritual', planet: 'ketu', type:'ketu', lord:'Ketu', grad:['#D9CFEA','#9E8AC7','#5A4A82','#2A1F4A'], color: '#9E8AC7', glowColor: 'rgba(158,138,199,0.38)', angle: 285, orbitSpeed: 55, sizeRatio: 1.0 },
  { id: 8, name: 'Travel', domainKey: 'travel', planet: 'mars', type:'mars', lord:'Mangal', grad:['#FFBAA0','#E85C3A','#9E2818','#4A0E08'], color: '#E85C3A', glowColor: 'rgba(232,92,58,0.4)', angle: 330, orbitSpeed: 40, sizeRatio: 0.85 },
];

// Realistic planet visual rendering - each planet is a mini CSS artwork
const PlanetButton = ({ 
  size = 44, 
  color, 
  glowColor, 
  planet,
  name,
  isReady = false, 
  isLoading = false, 
  isLocked = false,
  langsReady = 0,
  progress = 0,
  onClick,
  sizeRatio = 1,
}) => {
  const planetSize = Math.round(size * sizeRatio);
  
  // Each planet renders its own unique visual via inline styles
  const renderPlanetSurface = () => {
    const common = { width: planetSize, height: planetSize, borderRadius: '50%', position: 'absolute', inset: 0 };
    switch (planet) {
      case 'earth':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #3a8abf 0%, #1f6090 35%, #0e3d60 60%, #082438 80%, #041420 100%)' }} />
            <div style={{ ...common, background: 'radial-gradient(ellipse at 40% 35%, rgba(45,130,70,0.4) 0%, transparent 28%), radial-gradient(ellipse at 60% 52%, rgba(45,130,70,0.3) 0%, transparent 22%)' }} />
            <div style={{ ...common, background: 'radial-gradient(ellipse at 52% 25%, rgba(220,230,240,0.14) 0%, transparent 32%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 8px rgba(0,0,0,0.5), inset 2px 2px 4px rgba(70,150,210,0.08)' }} />
          </>
        );
      case 'venus':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #dcc090 0%, #b89860 30%, #907040 55%, #604828 80%, #382818 100%)' }} />
            <div style={{ ...common, background: 'linear-gradient(155deg, transparent 10%, rgba(220,200,160,0.12) 30%, transparent 50%, rgba(190,170,120,0.08) 70%, transparent 90%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 7px rgba(0,0,0,0.4), inset 2px 2px 3px rgba(220,195,150,0.06)' }} />
          </>
        );
      case 'saturn':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #d8c498 0%, #b8a070 30%, #907848 55%, #685030 80%, #402820 100%)' }} />
            <div style={{ ...common, background: 'linear-gradient(0deg, transparent 8%, rgba(160,130,70,0.15) 18%, transparent 25%, rgba(180,150,90,0.1) 38%, transparent 45%, rgba(150,120,60,0.08) 58%, transparent 65%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 7px rgba(0,0,0,0.4), inset 1px 1px 3px rgba(220,195,140,0.06)' }} />
            <div style={{
              position: 'absolute', width: planetSize * 1.9, height: planetSize * 0.6,
              left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotateX(68deg)',
              zIndex: 5, pointerEvents: 'none',
            }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'radial-gradient(ellipse, transparent 42%, rgba(180,155,100,0.35) 47%, rgba(190,165,110,0.22) 54%, transparent 58%, rgba(165,140,85,0.2) 63%, rgba(175,150,95,0.14) 70%, transparent 76%)',
              }} />
            </div>
          </>
        );
      case 'jupiter':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #c8a078 0%, #a07850 30%, #785838 55%, #503820 80%, #302010 100%)' }} />
            <div style={{ ...common, background: 'linear-gradient(0deg, rgba(90,50,25,0.22) 8%, transparent 15%, rgba(150,110,65,0.15) 22%, transparent 30%, rgba(110,65,30,0.2) 38%, transparent 46%, rgba(160,120,70,0.1) 54%, transparent 62%, rgba(100,55,28,0.18) 70%, transparent 78%)' }} />
            <div style={{ position: 'absolute', width: '20%', height: '12%', top: '56%', left: '56%', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(150,50,25,0.3) 0%, transparent 65%)', transform: 'rotate(-15deg)' }} />
            <div style={{ ...common, boxShadow: 'inset -4px -4px 8px rgba(0,0,0,0.45), inset 2px 2px 3px rgba(190,150,100,0.05)' }} />
          </>
        );
      case 'moon':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 32% 32%, #c8c4be 0%, #a8a49e 30%, #888480 55%, #686460 80%, #484440 100%)' }} />
            <div style={{ ...common, background: 'radial-gradient(circle at 55% 40%, rgba(0,0,0,0.08) 0%, transparent 10%), radial-gradient(circle at 35% 65%, rgba(0,0,0,0.1) 0%, transparent 8%), radial-gradient(circle at 68% 58%, rgba(0,0,0,0.06) 0%, transparent 11%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 6px rgba(0,0,0,0.4), inset 2px 2px 3px rgba(220,220,215,0.05)' }} />
          </>
        );
      case 'mercury':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 32% 32%, #a09890 0%, #807870 35%, #605850 60%, #403830 80%, #282420 100%)' }} />
            <div style={{ ...common, background: 'radial-gradient(circle at 48% 38%, rgba(0,0,0,0.1) 0%, transparent 8%), radial-gradient(circle at 62% 58%, rgba(0,0,0,0.08) 0%, transparent 6%), radial-gradient(circle at 32% 62%, rgba(0,0,0,0.12) 0%, transparent 9%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 5px rgba(0,0,0,0.5), inset 1px 1px 2px rgba(180,180,175,0.04)' }} />
          </>
        );
      case 'neptune':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #4a78b8 0%, #2e5898 30%, #163878 55%, #0a2058 80%, #041038 100%)' }} />
            <div style={{ ...common, background: 'linear-gradient(170deg, transparent 15%, rgba(70,115,185,0.1) 30%, transparent 48%, rgba(55,95,165,0.06) 62%, transparent 78%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 7px rgba(0,0,0,0.5), inset 2px 2px 3px rgba(70,120,200,0.06)' }} />
          </>
        );
      case 'mars':
        return (
          <>
            <div style={{ ...common, background: 'radial-gradient(circle at 30% 30%, #c87058 0%, #a04838 30%, #783020 55%, #502010 80%, #300808 100%)' }} />
            <div style={{ ...common, background: 'radial-gradient(ellipse at 45% 48%, rgba(60,22,10,0.15) 0%, transparent 22%), radial-gradient(circle at 62% 35%, rgba(50,18,8,0.1) 0%, transparent 14%)' }} />
            <div style={{ position: 'absolute', width: '32%', height: '9%', top: '4%', left: '34%', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(200,190,180,0.18) 0%, transparent 65%)' }} />
            <div style={{ ...common, boxShadow: 'inset -3px -3px 6px rgba(0,0,0,0.45), inset 1px 1px 3px rgba(210,130,90,0.04)' }} />
          </>
        );
      default:
        return <div style={{ ...common, background: `radial-gradient(circle at 30% 30%, ${color}, ${color}88)` }} />;
    }
  };

  return (
    <button 
      onClick={onClick}
      disabled={isLoading}
      className={`relative group transition-all duration-300 ${
        isLoading ? 'cursor-wait' : isReady ? 'hover:scale-110 active:scale-95 cursor-pointer' : isLocked ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-50'
      }`}
      style={{ width: planetSize, height: planetSize }}
      data-testid={`planet-${name?.toLowerCase()}`}
    >
      {/* Atmospheric glow */}
      <div 
        className={`absolute rounded-full transition-all duration-500 ${
          isReady ? 'opacity-70 scale-[1.5]' : isLoading ? 'opacity-40 scale-[1.35] animate-pulse' : 'opacity-20 scale-[1.2]'
        } group-hover:opacity-90 group-hover:scale-[1.6]`}
        style={{ 
          inset: 0,
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`, 
          filter: 'blur(6px)' 
        }}
      />
      
      {/* Planet body */}
      <div 
        className="absolute rounded-full overflow-hidden"
        style={{ 
          inset: 0,
          width: planetSize, height: planetSize,
          opacity: isLocked ? 0.5 : isLoading ? 0.7 : 1,
          transition: 'opacity 0.3s',
        }}
      >
        {renderPlanetSurface()}
        
        {/* Universal light highlight - top-left sun reflection */}
        <div className="absolute rounded-full" style={{
          width: '30%', height: '30%', top: '8%', left: '14%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 50%, transparent 70%)',
          zIndex: 2,
        }} />
        
        {/* Terminator shadow (dark side) */}
        <div className="absolute rounded-full" style={{
          inset: 0, width: '100%', height: '100%',
          background: 'linear-gradient(135deg, transparent 40%, rgba(0,0,0,0.25) 65%, rgba(0,0,0,0.5) 85%)',
          zIndex: 3,
        }} />
      </div>
      
      {/* Icon overlay (lock / loading / play) */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 rounded-full">
        {isLocked ? (
          <Lock style={{ width: planetSize * 0.28, height: planetSize * 0.28, color: 'rgba(255,255,255,0.5)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }} />
        ) : isLoading ? (
          <Loader2 className="animate-spin" style={{ width: planetSize * 0.3, height: planetSize * 0.3, color: '#fff', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }} />
        ) : isReady ? (
          <Play 
            className="transition-all duration-300 group-hover:scale-110"
            style={{ width: planetSize * 0.36, height: planetSize * 0.36, color: '#fff', filter: 'drop-shadow(0 1px 5px rgba(0,0,0,0.7))' }}
            fill="currentColor" strokeWidth={0}
          />
        ) : null}
      </div>
    </button>
  );
};

// Mystical loading phrases — rotate during Future Reels generation (same vibe as Past Story)
const LOADING_PHRASES = [
  'Aligning the celestial bodies...',
  'Reading your planetary positions...',
  'The stars whisper your future...',
  'Mapping karma across lifetimes...',
  'Decoding planetary influences...',
  'Channeling ancient Vedic wisdom...',
  'Your cosmic blueprint is forming...',
];

// Quick Questions for Sage - Arc layout with responsive positioning
const QUICK_QUESTIONS = [
  { id: 1, text: "Wealth?", icon: "♃", position: 'top' },
  { id: 2, text: "Career?", icon: "♄", position: 'top-left' },
  { id: 3, text: "Travel?", icon: "♂", position: 'top-right' },
  { id: 4, text: "Marriage?", icon: "♀", position: 'left' },
  { id: 5, text: "Health?", icon: "☉", position: 'right' },
  { id: 6, text: "Job?", icon: "♄", position: 'bottom-left' },
  { id: 7, text: "Business?", icon: "♃", position: 'bottom-right' },
];

// Interactive Hex Play Button - Combines hex styling with play functionality
// Uses domain color subtly, no green checkmarks, clean symbol placement
// (Old HexPlayButton removed — replaced by PlanetButton in solar system config above)

// Background Stars Component - Pre-computed positions for stable rendering
const STAR_POSITIONS = [
  { id: 0, left: '5%', top: '8%', size: 2, delay: 0.5, duration: 4, opacity: 0.5 },
  { id: 1, left: '12%', top: '22%', size: 1.5, delay: 1.2, duration: 5, opacity: 0.4 },
  { id: 2, left: '25%', top: '15%', size: 2.5, delay: 0.8, duration: 3.5, opacity: 0.6 },
  { id: 3, left: '38%', top: '5%', size: 1.8, delay: 2.1, duration: 4.5, opacity: 0.45 },
  { id: 4, left: '52%', top: '18%', size: 2.2, delay: 1.5, duration: 5.5, opacity: 0.55 },
  { id: 5, left: '65%', top: '10%', size: 1.6, delay: 0.3, duration: 4.2, opacity: 0.5 },
  { id: 6, left: '78%', top: '25%', size: 2.8, delay: 2.8, duration: 3.8, opacity: 0.65 },
  { id: 7, left: '88%', top: '12%', size: 1.4, delay: 1.8, duration: 6, opacity: 0.35 },
  { id: 8, left: '92%', top: '32%', size: 2.1, delay: 0.9, duration: 4.8, opacity: 0.52 },
  { id: 9, left: '8%', top: '42%', size: 1.9, delay: 3.2, duration: 5.2, opacity: 0.48 },
  { id: 10, left: '18%', top: '55%', size: 2.3, delay: 1.1, duration: 4.1, opacity: 0.58 },
  { id: 11, left: '32%', top: '48%', size: 1.7, delay: 2.5, duration: 3.6, opacity: 0.42 },
  { id: 12, left: '45%', top: '38%', size: 2.6, delay: 0.6, duration: 5.8, opacity: 0.62 },
  { id: 13, left: '58%', top: '52%', size: 1.3, delay: 1.9, duration: 4.4, opacity: 0.38 },
  { id: 14, left: '72%', top: '45%', size: 2.4, delay: 3.5, duration: 3.9, opacity: 0.56 },
  { id: 15, left: '85%', top: '58%', size: 1.5, delay: 0.4, duration: 5.1, opacity: 0.44 },
  { id: 16, left: '95%', top: '48%', size: 2.0, delay: 2.2, duration: 4.6, opacity: 0.5 },
  { id: 17, left: '3%', top: '68%', size: 2.7, delay: 1.4, duration: 3.4, opacity: 0.64 },
  { id: 18, left: '15%', top: '75%', size: 1.6, delay: 3.8, duration: 5.4, opacity: 0.4 },
  { id: 19, left: '28%', top: '82%', size: 2.2, delay: 0.7, duration: 4.3, opacity: 0.54 },
  { id: 20, left: '42%', top: '72%', size: 1.8, delay: 2.9, duration: 5.7, opacity: 0.46 },
  { id: 21, left: '55%', top: '85%', size: 2.5, delay: 1.6, duration: 3.7, opacity: 0.6 },
  { id: 22, left: '68%', top: '78%', size: 1.4, delay: 4.1, duration: 4.9, opacity: 0.36 },
  { id: 23, left: '82%', top: '88%', size: 2.1, delay: 0.2, duration: 5.3, opacity: 0.52 },
  { id: 24, left: '93%', top: '72%', size: 1.9, delay: 2.6, duration: 4.0, opacity: 0.48 },
  { id: 25, left: '22%', top: '35%', size: 1.2, delay: 1.3, duration: 6.2, opacity: 0.32 },
  { id: 26, left: '48%', top: '62%', size: 2.9, delay: 3.1, duration: 3.3, opacity: 0.68 },
  { id: 27, left: '75%', top: '35%', size: 1.7, delay: 0.1, duration: 5.6, opacity: 0.42 },
  { id: 28, left: '35%', top: '92%', size: 2.3, delay: 2.4, duration: 4.7, opacity: 0.58 },
  { id: 29, left: '62%', top: '28%', size: 1.5, delay: 1.7, duration: 5.9, opacity: 0.4 },
];

const StarsBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {STAR_POSITIONS.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full animate-pulse"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            background: `radial-gradient(circle, rgba(255,255,255,${star.opacity}) 0%, transparent 70%)`,
            boxShadow: `0 0 ${star.size * 2}px rgba(255,255,255,${star.opacity * 0.5})`,
            animationDelay: `${star.delay}s`,
            animationDuration: `${star.duration}s`,
          }}
        />
      ))}
      
      {/* Larger accent stars */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div
          key={`accent-${i}`}
          className="absolute"
          style={{
            left: `${10 + (i * 12)}%`,
            top: `${15 + (i % 3) * 30}%`,
            animation: `twinkle ${3 + i % 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path 
              d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" 
              fill={`rgba(255,255,255,${0.3 + (i % 3) * 0.15})`}
            />
          </svg>
        </div>
      ))}
    </div>
  );
};

// Coming Soon Features
const COMING_SOON_FEATURES = [
  { id: 'kundli-milan', title: 'Kundli Milan', features: ['Match Score', 'Compatibility', 'Timing', 'Spouse Nature'] },
  { id: 'corporate-jyotish', title: 'Corporate Jyotish', features: ['Growth Cycles', 'Funding', 'Launch Timing', 'Risk Analysis'] },
];

const ComingSoonCard = ({ feature, isTinyMobile }) => {
  return (
    <div 
      className="flex-1 min-w-0"
      style={{ 
        borderRadius: '8px',
        background: 'rgba(18,24,42,0.85)',
        border: '0.5px solid rgba(200,175,120,0.12)',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
      }}
      data-testid={`coming-soon-${feature.id}`}
    >
      {/* Header */}
      <div className="px-2.5 pt-1.5 pb-0.5 flex items-center justify-between gap-1">
        <p style={{ fontSize: isTinyMobile ? '9px' : '10px', fontWeight: 600, color: 'rgba(220,200,160,0.85)', lineHeight: 1.1, letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {feature.title}
        </p>
        <div className="flex-shrink-0 px-1 py-0.5 rounded-full" style={{
          background: 'rgba(200,175,120,0.08)',
          border: '0.5px solid rgba(200,175,120,0.18)',
        }}>
          <span style={{ fontSize: '5px', fontWeight: 700, color: 'rgba(200,175,120,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Soon
          </span>
        </div>
      </div>
      {/* Feature tags — single row, truncate with ellipsis to save vertical space */}
      <div className="px-2.5 pb-1.5 flex gap-1 overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
        {feature.features.slice(0, 2).map((f, i) => (
          <span key={i} style={{ 
            fontSize: isTinyMobile ? '6.5px' : '7px', 
            color: 'rgba(255,255,255,0.4)', 
            fontWeight: 500,
            padding: '1.5px 4px',
            borderRadius: '3px',
            background: 'rgba(255,255,255,0.04)',
            border: '0.5px solid rgba(255,255,255,0.04)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
          }}>
            {f}
          </span>
        ))}
        {feature.features.length > 2 && (
          <span style={{ fontSize: isTinyMobile ? '6.5px' : '7px', color: 'rgba(255,255,255,0.3)', alignSelf: 'center' }}>
            +{feature.features.length - 2}
          </span>
        )}
      </div>
    </div>
  );
};

const HexStory = () => {
  const navigate = useNavigate();
  const { isPremium, isAuthenticated, user, isLoading, refreshSession } = useSession();
  
  // Domain readiness state - Initialize from cache to prevent UI glitch when switching tabs
  const [domainStatus, setDomainStatus] = useState(() => {
    try {
      const cached = localStorage.getItem('domain_tts_status');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });
  const [generationTriggered, setGenerationTriggered] = useState(() => {
    return localStorage.getItem('tts_generation_triggered') === 'true';
  });
  // Per-domain loading progress: { health: 0, career: 0, ... } — persisted to localStorage
  // When payment_generation_active is set, start fresh (stale data cleared by PaymentPlans)
  const [domainProgress, setDomainProgress] = useState(() => {
    try {
      // If payment just happened, PaymentPlans already cleared domain_progress
      const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
      if (paymentActive) return {};
      const stored = JSON.parse(localStorage.getItem('domain_progress') || '{}');
      // Clean up stale partial progress: if TTS isn't actively generating AND a domain is
      // stuck at a mid-value (5% or similar) without being fully cached, reset it to 0 so
      // the UI doesn't show a permanent "5%" ghost when generation actually restarts.
      const ttsActive = localStorage.getItem('tts_currently_generating') === 'true';
      const readyDomains = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
      if (!ttsActive) {
        const cleaned = {};
        for (const [k, v] of Object.entries(stored)) {
          if (readyDomains[k] || v === 100) cleaned[k] = 100;
          // anything partially-stuck gets reset to 0 (or dropped)
        }
        localStorage.setItem('domain_progress', JSON.stringify(cleaned));
        return cleaned;
      }
      return stored;
    } catch { return {}; }
  });
  
  // Intro animation state - to show users the questions feature
  const [showIntroAnimation, setShowIntroAnimation] = useState(false);
  const [introAnimationPlayed, setIntroAnimationPlayed] = useState(() => {
    return sessionStorage.getItem('storyIntroPlayed') === 'true';
  });
  
  // Sage Q&A State - INLINE (no modal). Persisted across tab switches / app closes
  // so the current answer + 3-language TTS stays until the user sends a new question.
  const SAGE_CACHE_KEY = 'sage_last_answer_v1';
  const [sageExpanded, setSageExpanded] = useState(false);
  const [sageQuestion, setSageQuestion] = useState('');
  const [sageAnswer, setSageAnswer] = useState(() => {
    try {
      const raw = localStorage.getItem(SAGE_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.sageAnswer || null;
    } catch { return null; }
  });
  const [sageLoading, setSageLoading] = useState(false);
  const [sageCredits, setSageCredits] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAnswer, setIsPlayingAnswer] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [conversationCount, setConversationCount] = useState(0);
  
  // Credits Refill Modal state
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  
  // Rotating mystical phrase during loading (matches Past Story's engagement)
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx(i => (i + 1) % LOADING_PHRASES.length), 3500);
    return () => clearInterval(t);
  }, []);

  // Persist sage answer + TTS audio across tab switches / app closes so the user
  // can come back to their last question & voices without re-typing. Cleared only
  // when a new question is sent (see handleAskSage).
  useEffect(() => {
    try {
      if (sageAnswer) {
        localStorage.setItem(SAGE_CACHE_KEY, JSON.stringify({
          sageAnswer,
          audioData: audioDataRef.current || {},
          savedAt: Date.now(),
        }));
      } else {
        localStorage.removeItem(SAGE_CACHE_KEY);
      }
    } catch {
      // Quota exceeded — strip large audio base64 and retry with text-only.
      try {
        if (sageAnswer) {
          localStorage.setItem(SAGE_CACHE_KEY, JSON.stringify({
            sageAnswer: { ...sageAnswer, audioAllLanguages: undefined },
            audioData: {},
            savedAt: Date.now(),
          }));
        }
      } catch {}
    }
  }, [sageAnswer]);
  
  const audioRef = useRef(null);
  const audioDataRef = useRef((() => {
    try {
      const raw = localStorage.getItem('sage_last_answer_v1');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed?.audioData || {};
    } catch { return {}; }
  })()); // Holds latest audioAllLanguages to avoid stale closures
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  // Per-language audio position tracking for pause/resume
  const audioPositionsRef = useRef({});
  const currentAudioLangRef = useRef(null);
  
  // Resolve userId — prefer Firebase fb_* over device-derived native-* / web-* / session_*
  // so Sage QA, refill, and credit endpoints all hit the same canonical doc the IAP grants
  // credits to. Without this filter, /api/sage/ask was deducting from a separate native-*
  // doc with stale low credits while real balance lived in fb_* (split-brain bug).
  const _rawUserId = user?.user_id || localStorage.getItem('user_id');
  const userId = (_rawUserId && !/^(native-|web-|session_|device_|taraanaya_device_)/.test(_rawUserId))
    ? _rawUserId
    : null;
  const isUnlocked = isLoading || isPremium;
  const futureReelsUnlocked = isPremium && isFeatureUnlocked('future_reels');
  
  // Track future reels generation status
  const [reelsGenerating, setReelsGenerating] = useState(() => {
    // Clear stuck generating state (if stuck for more than 2 minutes)
    const genStart = localStorage.getItem('future_reels_gen_start');
    if (genStart && Date.now() - Number(genStart) > 120000) {
      localStorage.removeItem('future_reels_generating');
      localStorage.removeItem('future_reels_gen_start');
      return false;
    }
    return localStorage.getItem('future_reels_generating') === 'true';
  });
  const [reelsReady, setReelsReady] = useState(() => {
    // Check if actual domain reels content exists in any format
    try {
      const ud = verifyAndGetCachedStory() || {};
      const reels = ud?.future_story?.domain_reels || ud?.story_data?.future_story?.domain_reels || {};
      // Check both formats: {lang: {domain: text}} and {domain: {lang: text}}
      const hasContent = Object.values(reels).some(lr => {
        if (typeof lr === 'string' && lr.length > 50) return true;
        if (typeof lr === 'object' && lr) {
          return Object.values(lr).some(v => typeof v === 'string' && v.length > 50);
        }
        return false;
      });
      return hasContent;
    } catch(e) { return false; }
  });
  
  // Listen for future reels ready event
  // PURE LOCALSTORAGE POLLER — No events, no closures, no race conditions
  // initializeAfterPayment() writes to localStorage, this poller reads every 1 second
  useEffect(() => {
    // Track dashboard page view
    trackPageView('dashboard');
    trackEvent('dashboard_viewed', { is_premium: localStorage.getItem('isPremium') === 'true' });
    const checkReels = () => {
      const generating = localStorage.getItem('future_reels_generating') === 'true';
      const genStart = localStorage.getItem('future_reels_gen_start');
      if (generating && genStart && Date.now() - Number(genStart) > 300000) {
        localStorage.removeItem('future_reels_generating');
        localStorage.removeItem('future_reels_gen_start');
        setReelsGenerating(false);
      } else {
        setReelsGenerating(generating);
      }
      try {
        const ud = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
        const reels = ud?.future_story?.domain_reels || ud?.story_data?.future_story?.domain_reels || {};
        const ready = Object.values(reels).some(lr => {
          if (typeof lr === 'string' && lr.length > 50) return true;
          if (typeof lr === 'object' && lr) {
            return Object.values(lr).some(v => typeof v === 'string' && v.length > 50);
          }
          return false;
        });
        setReelsReady(ready);
      } catch(e) { /* ignore */ }
    };
    
    // One-time reels check on mount
    checkReels();
    window.addEventListener('future-reels-ready', checkReels);
    window.addEventListener('storage', checkReels);
    
    // FAST POLLER: Read progress + ready state from localStorage every 1 second
    // This is the ONLY way HexStory learns about TTS progress — no events needed
    const pollInterval = setInterval(() => {
      try {
        // Read progress from localStorage (written by initializeAfterPayment)
        const storedProgress = JSON.parse(localStorage.getItem('domain_progress') || '{}');
        setDomainProgress(storedProgress);
        
        // Read audio ready domains from localStorage
        const storedReady = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
        setAudioReadyDomains(storedReady);
        
        // Track generation active state
        const ttsActive = localStorage.getItem('tts_currently_generating') === 'true';
        const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
        setGenerationActive(ttsActive || paymentActive);
        
        // Check if all 8 domains ready
        const allDomains = ['health','career','wealth','marriage','family','education','travel','spiritual'];
        const allDone = allDomains.every(d => storedReady[d] === true);
        setAudioAllReady(allDone);
        
        // AUTO-RETRY: If generation locks cleared but NOT all audio ready,
        // and unified story text exists, auto-trigger TTS generation
        if (!allDone && !ttsActive && !paymentActive) {
          // Only auto-generate Future Reels audio if user has paid ₹599+ (premium/ultimate)
          const unlockedFeatures = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
          const hasFutureReels = unlockedFeatures.includes('future_reels');
          if (!hasFutureReels) {
            // User hasn't paid for Future Reels — skip TTS generation
          } else {
          const storyDataStr = localStorage.getItem('unified_story_data');
          const readyCount = allDomains.filter(d => storedReady[d] === true).length;
          if (storyDataStr && readyCount < 8) {
            try {
              const storyData = JSON.parse(storyDataStr);
              const domainReels = storyData.future_story?.domain_reels || storyData.story_data?.future_story?.domain_reels;
              if (domainReels && Object.keys(domainReels).length > 0) {
                // Check if we already triggered recovery recently (avoid spam)
                const lastRetry = localStorage.getItem('tts_retry_timestamp');
                const now = Date.now();
                if (!lastRetry || (now - Number(lastRetry)) > 10000) { // 10s cooldown
                  console.log(`[HexStory] Auto-retry: ${readyCount}/8 ready, generation idle. Triggering TTS...`);
                  localStorage.setItem('tts_retry_timestamp', String(now));
                  const genUserId = localStorage.getItem('user_id') || localStorage.getItem('tts_user_id') || localStorage.getItem('taraanaya_device_id');
                  if (genUserId) {
                    startSequentialGeneration(genUserId, domainReels,
                      () => {}, // progress callback not needed, poller reads localStorage
                      () => {}  // complete callback not needed
                    );
                  }
                }
              }
            } catch (e) { console.log('[HexStory] Auto-retry parse error:', e); }
          }
          } // end hasFutureReels check
        }
      } catch {}
      
      // Also check reels status
      const stillGenerating = localStorage.getItem('future_reels_generating') === 'true';
      if (stillGenerating) checkReels();
    }, 1000);
    
    return () => {
      window.removeEventListener('future-reels-ready', checkReels);
      window.removeEventListener('storage', checkReels);
      clearInterval(pollInterval);
    };
  }, []);

  // STALE LOCK RECOVERY + BACKEND SYNC — runs once on mount to recover from
  // app-close-mid-generation and to rehydrate state after localStorage wipe.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Clear stale TTS lock (> 3 min old) so auto-retry can run
      const locked = localStorage.getItem('tts_currently_generating') === 'true';
      const lockTs = Number(localStorage.getItem('tts_lock_start_ts') || '0');
      const lockAgeMs = Date.now() - lockTs;
      if (locked && (lockTs === 0 || lockAgeMs > 180000)) {
        console.log('[HexStory] Clearing stale TTS lock (age=' + Math.round(lockAgeMs/1000) + 's)');
        localStorage.removeItem('tts_currently_generating');
        localStorage.removeItem('tts_lock_start_ts');
        // Reset any stuck partial progress to 0 so planet doesn't show permanent 5%
        try {
          const stuck = JSON.parse(localStorage.getItem('domain_progress') || '{}');
          const readyMap = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
          const cleaned = {};
          for (const [k, v] of Object.entries(stuck)) {
            if (readyMap[k] === true || v === 100) cleaned[k] = 100;
          }
          localStorage.setItem('domain_progress', JSON.stringify(cleaned));
          if (!cancelled) setDomainProgress(cleaned);
        } catch {}
      }
      
      // 2) Sync audio_ready_domains from backend (survives localStorage wipe / reinstall)
      const syncUserId = user?.user_id || localStorage.getItem('user_id') || getGenerationUserId();
      if (!syncUserId) return;
      // Skip ghost/device IDs to avoid polluting Railway logs
      if (/^(native-|web-|session_|device_|taraanaya_device_)/.test(syncUserId)) return;
      try {
        const status = await getDomainStatus(syncUserId);
        if (cancelled) return;
        if (status?.success && status?.domains) {
          const backendReady = {};
          for (const [domain, langMap] of Object.entries(status.domains)) {
            // Domain is "ready" if all 3 languages are marked true in backend
            const allLangs = ['hinglish','hindi','english'].every(l => langMap?.[l] === true);
            if (allLangs) backendReady[domain] = true;
          }
          if (Object.keys(backendReady).length > 0) {
            // Merge with any existing localStorage-tracked readiness
            const existing = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
            const merged = { ...existing, ...backendReady };
            localStorage.setItem('audio_ready_domains', JSON.stringify(merged));
            if (!cancelled) {
              setAudioReadyDomains(merged);
              const allDomains = ['health','career','wealth','marriage','family','education','travel','spiritual'];
              if (allDomains.every(d => merged[d])) setAudioAllReady(true);
            }
            console.log('[HexStory] Synced audio_ready_domains from backend:', Object.keys(backendReady));
          }
        }
      } catch (e) {
        console.log('[HexStory] Backend sync failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // AUTO-REGENERATE: If premium user has no reels data (cache cleared), auto-trigger generation
  const autoRegenTriggeredRef = useRef(false);
  useEffect(() => {
    if (isLoading) return; // Don't trigger during loading state
    if (!isPremium) return; // Must be confirmed premium from session (not stale localStorage)
    // Skip if payment handler is already managing generation (prevents competing API calls)
    const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
    if (paymentActive) {
      console.log('[HexStory] Skipping auto-regen — payment handler is managing generation');
      return;
    }
    if (futureReelsUnlocked && !reelsReady && !reelsGenerating && !autoRegenTriggeredRef.current) {
      autoRegenTriggeredRef.current = true;
      console.log('Auto-regenerating future reels (premium user, no cached data)...');
      setReelsGenerating(true);
      localStorage.setItem('future_reels_generating', 'true');
      localStorage.setItem('future_reels_gen_start', String(Date.now()));
      
      generatePremiumContent(['future_reels'])
        .then((result) => {
          if (result?.success) {
            console.log('Auto-regeneration complete');
            toast.success('Future Reels ready!');
            try {
              const ud = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
              const reels = ud?.future_story?.domain_reels || {};
              const ready = Object.values(reels).some(lr => Object.values(lr || {}).some(v => typeof v === 'string' && v.length > 50));
              setReelsReady(ready);
              localStorage.setItem('future_reels_ready', 'true');
              window.dispatchEvent(new Event('future-reels-ready'));
            } catch(e) { /* fallback */ }
          } else {
            console.warn('Auto-regeneration result:', result?.error);
            // Silently log — don't show error toast to avoid confusing non-premium users
          }
        })
        .catch((err) => {
          console.error('Auto-regeneration error:', err);
        })
        .finally(() => {
          setReelsGenerating(false);
          localStorage.removeItem('future_reels_generating');
        });
    }
  }, [futureReelsUnlocked, reelsReady, reelsGenerating, isPremium]);

  // Track audio readiness per domain via IndexedDB + localStorage
  const [audioReadyDomains, setAudioReadyDomains] = useState(() => {
    // When payment just happened, start fresh (PaymentPlans cleared audio_ready_domains)
    try {
      const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
      if (paymentActive) return {};
      const saved = localStorage.getItem('audio_ready_domains');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [audioAllReady, setAudioAllReady] = useState(() => {
    try {
      const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
      if (paymentActive) return false;
      const saved = localStorage.getItem('audio_ready_domains');
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      const allDomains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
      return allDomains.every(d => parsed[d]);
    } catch { return false; }
  });
  const audioGeneratingRef = useRef(false);
  
  // Persist audioReadyDomains to localStorage (only write if we have positive data)
  useEffect(() => {
    const hasAnyReady = Object.values(audioReadyDomains).some(v => v === true);
    if (hasAnyReady) {
      localStorage.setItem('audio_ready_domains', JSON.stringify(audioReadyDomains));
    }
  }, [audioReadyDomains]);

  // Track generation state via polling (same poller that reads progress)
  const [generationActive, setGenerationActive] = useState(() => {
    return localStorage.getItem('tts_currently_generating') === 'true' || 
           localStorage.getItem('payment_generation_active') === 'true';
  });

  // Responsive layout calculations
  // Store INITIAL height separately - keyboard must NOT change it
  const initialHeightRef = useRef(window.innerHeight || 812);
  const [screenSize, setScreenSize] = useState({ width: window.innerWidth || 375, height: window.innerHeight || 812 });
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  
  useEffect(() => {
    // Capture the true viewport height ONCE on mount (before keyboard)
    initialHeightRef.current = window.innerHeight;
    setScreenSize({ width: window.innerWidth, height: window.innerHeight });
    
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      // Detect keyboard: if height shrinks by >25% but width stays same = keyboard
      const heightRatio = newHeight / initialHeightRef.current;
      if (heightRatio < 0.75 && Math.abs(newWidth - screenSize.width) < 10) {
        setKeyboardOpen(true);
        // Only update width, keep original height to prevent squishing
        setScreenSize(prev => ({ ...prev, width: newWidth }));
      } else {
        setKeyboardOpen(false);
        // Full resize (orientation change, etc)
        initialHeightRef.current = newHeight;
        setScreenSize({ width: newWidth, height: newHeight });
      }
    };
    
    // Also detect keyboard via visualViewport API (more reliable)
    if (window.visualViewport) {
      const handleViewportResize = () => {
        const vpHeight = window.visualViewport.height;
        const isKb = vpHeight < initialHeightRef.current * 0.75;
        setKeyboardOpen(isKb);
      };
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.addEventListener('resize', handleResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.removeEventListener('resize', handleResize);
      };
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Layout constants - responsive based on screen size
  // Desktop/tablet breakpoints for proper scaling
  const isMobile = screenSize.width < 400;
  const isSmallMobile = screenSize.width < 360;
  const isTinyMobile = screenSize.width < 330;
  const isTablet = screenSize.width >= 400 && screenSize.width < 768;
  const isDesktop = screenSize.width >= 768 && screenSize.width < 1200;
  const isLargeDesktop = screenSize.width >= 1200;
  const radius = isSmallMobile ? 65 : isMobile ? 70 : 75;
  const centerX = 50;
  // Adjust centerY based on aspect ratio - move down on taller screens
  const aspectRatio = screenSize.height / screenSize.width;
  const centerY = isLargeDesktop ? 42 : isDesktop ? 42 : aspectRatio > 1.8 ? 38 : aspectRatio > 1.6 ? 40 : 42;
  
  // PIXEL-EXACT safe-zone centerY — shared by Sage avatar, 3D solar system, and 2D click overlay
  // so EVERY element sits inside the zone between header and Coming Soon cards without overlap.
  // Coming Soon cards (Kundli Milan + Corporate Jyotish) actually render ~140-150px tall on mobile.
  const _headerReserved = 90 + (isTinyMobile ? 6 : 10);
  const _bottomReserved = 70 /* nav */ + (isTinyMobile ? 55 : isSmallMobile || isMobile ? 60 : 65) /* compact coming-soon cards */ + 36 /* phrase */ + (isTinyMobile ? 6 : 10);
  const _availH = Math.max(200, screenSize.height - _headerReserved - _bottomReserved);
  // Shift system upward by biasing center slightly above mid-zone (0.45 instead of 0.5)
  const centerYpx = _headerReserved + _availH * 0.46;
  
  // Desktop height-aware sizing: cap solar system to fit viewport height
  // Reserve space for header (~60px) + bottom elements (~180px for coming soon + nav + spacing)
  const availableSystemHeight = (isDesktop || isLargeDesktop) ? screenSize.height - 260 : 9999;
  const idealSystemSize = isLargeDesktop ? 540 : isDesktop ? 480 : isTablet ? 400 : isMobile ? 320 : isSmallMobile ? 295 : isTinyMobile ? 270 : 320;
  const systemSize = Math.min(idealSystemSize, availableSystemHeight);
  const sizeScale = (isDesktop || isLargeDesktop) ? systemSize / idealSystemSize : 1;

  // Intro animation effect - briefly show questions to users
  useEffect(() => {
    if (!introAnimationPlayed && isUnlocked && !isLoading) {
      const timer1 = setTimeout(() => {
        setShowIntroAnimation(true);
        setSageExpanded(true);
      }, 800);
      
      const timer2 = setTimeout(() => {
        setSageExpanded(false);
        setShowIntroAnimation(false);
        setIntroAnimationPlayed(true);
        sessionStorage.setItem('storyIntroPlayed', 'true');
      }, 2500);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [introAnimationPlayed, isUnlocked, isLoading]);

  // Check unlock status on mount
  useEffect(() => {
    console.log('HexStory unlock status:', { isPremium, isAuthenticated, isLoading });
    // DO NOT auto-clean localStorage here.
    // Prior bug: this effect was wiping plan_unlocks + is_premium when `isPremium`
    // briefly flipped to false during session load race (fetchSession not finished
    // while isLoading=false). That caused the "unlocked for 2s then locked" bug
    // for users whose backend state IS premium but frontend hadn't synced yet.
    // Source of truth for premium is now the backend (verify-premium / subscription
    // endpoints). Frontend never wipes entitlements locally.
  }, [isPremium, isAuthenticated, user, isLoading]);

  // Poll for domain TTS status - reduced frequency since TTS is on-demand
  useEffect(() => {
    if (!isUnlocked) return;
    
    let isMounted = true;
    
    const pollStatus = async () => {
      if (!isMounted) return;
      
      const pollUserId = user?.user_id || localStorage.getItem('user_id') || getGenerationUserId();
      if (!pollUserId) return;
      // Skip ghost/device IDs — these pollute Railway logs and hit the wrong
      // user bucket. Only poll once we have a real authenticated id.
      if (/^(native-|web-|session_|device_|taraanaya_device_)/.test(pollUserId)) return;
      
      try {
        const status = await getDomainStatus(pollUserId);
        if (!isMounted) return;
        
        if (status?.success && status?.domains) {
          setDomainStatus(status.domains);
          localStorage.setItem('domain_tts_status', JSON.stringify(status.domains));
        }
      } catch (err) {
        // Silent fail
      }
    };
    
    pollStatus();
    // Poll every 10 seconds instead of 1 second (TTS is on-demand now)
    const interval = setInterval(pollStatus, 10000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [isUnlocked, user, generationTriggered]);

  // Fetch Sage credits - re-fetch when premium status changes (after payment)
  useEffect(() => {
    if (userId) {
      fetchSageCredits();
    }
    
    // Listen for credits update from payment
    const handleCreditsUpdate = () => fetchSageCredits();
    window.addEventListener('credits-updated', handleCreditsUpdate);
    return () => window.removeEventListener('credits-updated', handleCreditsUpdate);
  }, [userId, isUnlocked, isPremium]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSageQuestion(transcript);
        setIsRecording(false);
      };
      
      recognitionRef.current.onerror = () => setIsRecording(false);
      recognitionRef.current.onend = () => setIsRecording(false);
    }
    
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const fetchSageCredits = async () => {
    // Don't fetch if no authenticated user_id — device_id / session_id IDs
    // return 0 credits from backend and would flicker the UI badge.
    if (!userId || String(userId).startsWith('native-') || String(userId).startsWith('web-') || String(userId).startsWith('session_')) {
      const localCredits = parseInt(localStorage.getItem('sage_credits') || '0', 10);
      if (localCredits > 0) setSageCredits(localCredits);
      return;
    }
    try {
      // Show localStorage credits IMMEDIATELY (instant feedback)
      const localCredits = parseInt(localStorage.getItem('sage_credits') || '0', 10);
      if (localCredits > 0) {
        setSageCredits(localCredits);
      }
      
      // Then check backend for authoritative credits
      const response = await fetch(`${BACKEND_URL}/api/sage/credits/${userId}`);
      const data = await response.json();
      
      if (data.success) {
        const backendCredits = data.credits || 0;
        
        if (backendCredits > 0) {
          // Backend has credits - use them as source of truth
          setSageCredits(backendCredits);
          persistSageCredits(backendCredits);
          return;
        }
        
        // Backend has 0 but localStorage has credits → don't OVERWRITE to 0.
        // Only sync localStorage→backend if user is on a fresh post-purchase
        // state. Otherwise leave the UI showing the local value.
        if (localCredits > 0 && backendCredits === 0) {
          console.log(`Credits mismatch: localStorage=${localCredits}, backend=0. Syncing...`);
          try {
            const syncRes = await fetch(`${BACKEND_URL}/api/sage/sync-credits`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: userId, credits: localCredits })
            });
            const syncData = await syncRes.json();
            if (syncData.success) {
              setSageCredits(syncData.credits);
              persistSageCredits(syncData.credits);
              return;
            }
          } catch (syncErr) {
            console.error('Credits sync failed:', syncErr);
          }
          // Keep showing localStorage credits regardless
          setSageCredits(localCredits);
        }
      } else if (localCredits > 0) {
        setSageCredits(localCredits);
      }
    } catch (error) {
      // Fallback to localStorage
      const localCredits = parseInt(localStorage.getItem('sage_credits') || '0', 10);
      if (localCredits > 0) setSageCredits(localCredits);
    }
  };

  // Auto-start SEQUENTIAL generation when future reels unlocked (599/999 plans only)
  // Uses localStorage lock to prevent duplicate runs across re-mounts
  useEffect(() => {
    if (!futureReelsUnlocked || !reelsReady) return;
    if (audioAllReady) return; // All done, nothing to generate
    if (audioGeneratingRef.current) return;
    
    // Check if generation is already running (localStorage lock survives re-mounts)
    const generatingLock = localStorage.getItem('tts_currently_generating');
    if (generatingLock === 'true') {
      console.log('[HexStory] TTS generation already in progress (locked), skipping');
      return;
    }
    
    // Skip if payment handler is managing generation (prevents competing API calls)
    const paymentActive = localStorage.getItem('payment_generation_active') === 'true';
    if (paymentActive) {
      console.log('[HexStory] Skipping TTS auto-start — payment handler is managing generation');
      return;
    }
    
    const startGeneration = async () => {
      if (audioGeneratingRef.current) return;
      audioGeneratingRef.current = true;
      localStorage.setItem('tts_currently_generating', 'true');
      
      const genUserId = user?.user_id || localStorage.getItem('user_id') || getGenerationUserId();
      if (!genUserId) { 
        audioGeneratingRef.current = false; 
        localStorage.removeItem('tts_currently_generating');
        return; 
      }
      
      let storyDataStr = localStorage.getItem('unified_story_data');
      if (!storyDataStr) { 
        audioGeneratingRef.current = false; 
        localStorage.removeItem('tts_currently_generating');
        return; 
      }
      
      try {
        const storyData = JSON.parse(storyDataStr);
        const domainReels = storyData.future_story?.domain_reels || 
                            storyData.story_data?.future_story?.domain_reels;
        if (!domainReels) { 
          audioGeneratingRef.current = false; 
          localStorage.removeItem('tts_currently_generating');
          return; 
        }
        
        setGenerationTriggered(true);
        localStorage.setItem('tts_generation_triggered', 'true');
        localStorage.setItem('tts_user_id', genUserId);
        
        // Use SEQUENTIAL generation with progress callbacks
        // startSequentialGeneration automatically skips already-cached domains
        await startSequentialGeneration(
          genUserId,
          domainReels,
          // onDomainProgress
          (domainKey, progress) => {
            setDomainProgress(prev => ({ ...prev, [domainKey]: progress }));
          },
          // onDomainComplete
          (domainKey) => {
            setAudioReadyDomains(prev => {
              const updated = { ...prev, [domainKey]: true };
              localStorage.setItem('audio_ready_domains', JSON.stringify(updated));
              return updated;
            });
            setDomainProgress(prev => ({ ...prev, [domainKey]: 100 }));
          }
        );
        
        // Check if actually all ready after generation
        const readyDomains = JSON.parse(localStorage.getItem('audio_ready_domains') || '{}');
        const allDone = ['health','career','wealth','marriage','family','education','travel','spiritual'].every(d => readyDomains[d]);
        if (allDone) {
          setAudioAllReady(true);
        }
      } catch (err) {
        console.error('Error starting sequential generation:', err);
      } finally {
        audioGeneratingRef.current = false;
        localStorage.removeItem('tts_currently_generating');
      }
    };
    
    startGeneration();
  }, [futureReelsUnlocked, reelsReady, audioAllReady, user]);

  // Re-trigger TTS when new future reels arrive (e.g., after plan upgrade)
  useEffect(() => {
    const handleReelsReady = () => {
      // Only reset if not currently generating (prevent duplicate runs)
      if (audioGeneratingRef.current || localStorage.getItem('tts_currently_generating') === 'true') {
        console.log('[HexStory] Audio generation already in progress, skipping re-trigger');
        return;
      }
      console.log('[HexStory] Future reels ready event — re-triggering TTS generation');
      localStorage.removeItem('tts_generation_triggered');
      localStorage.removeItem('tts_currently_generating');
      setGenerationTriggered(false);
      setAudioAllReady(false);
    };
    window.addEventListener('future-reels-ready', handleReelsReady);
    return () => window.removeEventListener('future-reels-ready', handleReelsReady);
  }, []);

  // Cleanup generation lock on unmount (if this component was generating)
  useEffect(() => {
    return () => {
      // Don't remove lock on unmount — generation continues in background
      // Only clean up the ref
      // audioGeneratingRef.current stays false for next mount
    };
  }, []);

  const isDomainReady = (domainKey) => {
    if (!futureReelsUnlocked) return false;
    // Domain is ready ONLY when audio is cached in IndexedDB
    if (audioReadyDomains[domainKey]) return true;
    return false;
  };

  const isDomainLoading = (domainKey) => {
    if (!futureReelsUnlocked) return false;
    if (audioReadyDomains[domainKey]) return false;
    // Show loading when text content generation is in progress (before TTS)
    if (reelsGenerating && !reelsReady) return true;
    // Show loading for the domain that actually has progress (actively being processed)
    const progress = domainProgress[domainKey] || 0;
    if (progress > 0 && progress < 100) return true;
    // When generation is active (from state, updated by poller), show loading on all non-ready domains
    if (generationActive && progress < 100) return true;
    // FALLBACK: If future reels are unlocked but audio isn't ready for this domain,
    // and no other domains are ready either, show loading (generation just started or in gap)
    const anyDomainReady = Object.values(audioReadyDomains).some(v => v === true);
    if (!anyDomainReady && !audioAllReady) return true;
    return false;
  };

  const getDomainProgressPercent = (domainKey) => {
    if (audioReadyDomains[domainKey]) return 100;
    return domainProgress[domainKey] || 0;
  };
  
  const getLangsReady = (domainKey) => {
    const domainLangs = domainStatus[domainKey] || {};
    return ['hinglish', 'hindi', 'english'].filter(l => domainLangs[l]).length;
  };

  const handleDomainPlay = (domain) => {
    if (isLoading) {
      toast.info('Loading your account...');
      return;
    }
    if (!isPremium) {
      // Free user — show all plans
      navigate('/payment-plans');
      return;
    }
    if (!futureReelsUnlocked) {
      // Starter user (199) — show 599+999 plans for future reels
      navigate('/payment-plans?source=future_reels');
      return;
    }
    // Only allow navigation when audio is actually cached
    if (audioReadyDomains[domain.domainKey]) {
      trackEvent('domain_clicked', { domain: domain.domainKey, from: 'dashboard' });
      navigate(`/domain-story/${domain.domainKey}`);
      return;
    }
    // Domain not ready yet — show progress
    if (reelsGenerating || !reelsReady) {
      toast.info('Generating your Future Reels... please wait');
      return;
    }
    const progress = domainProgress[domain.domainKey] || 0;
    if (progress > 0) {
      toast.info(`${domain.name} loading audio... ${progress}%`);
    } else {
      toast.info(`${domain.name} is queued for audio generation. Please wait.`);
    }
  };

  // Sage Q&A Functions
  const handleSageClick = () => {
    if (!isUnlocked) {
      toast.error('Unlock premium to ask the Sage');
      return;
    }
    setSageExpanded(!sageExpanded);
    // Don't clear cached answer — preserve it for instant re-display
    if (!sageExpanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleQuickQuestion = (question) => {
    setSageQuestion(question);
    setTimeout(() => handleAskSage(question), 100);
  };

  const handleAskSage = async (questionOverride = null) => {
    const question = questionOverride || sageQuestion;
    if (!question.trim()) {
      toast.error('Please ask a question');
      return;
    }
    
    const creditCost = 100;
    if (sageCredits < creditCost) {
      toast.error(`Need ${creditCost} credits. Tap to refill!`);
      setShowCreditsModal(true);
      return;
    }
    
    setSageLoading(true);
    
    try {
      // Sage takes 30-60s (runs 8 domain engines + GPT + TTS) — use 120s timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      
      const response = await fetch(`${BACKEND_URL}/api/sage/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          user_id: userId,
          question: question,
          language: selectedLanguage,
          is_followup: conversationCount > 0,
          session_id: `session_${userId}_${new Date().toISOString().split('T')[0]}`
        })
      });
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      // Handle insufficient credits - try auto-sync and retry once
      if (!data.success && data.error === 'insufficient_credits') {
        const localCredits = parseInt(localStorage.getItem('sage_credits') || '0', 10);
        if (localCredits >= creditCost) {
          console.log('Backend credits mismatch. Syncing and retrying...');
          try {
            const syncRes = await fetch(`${BACKEND_URL}/api/sage/sync-credits`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: userId, credits: localCredits })
            });
            const syncData = await syncRes.json();
            if (syncData.success && syncData.credits >= creditCost) {
              // Retry the question
              const retryRes = await fetch(`${BACKEND_URL}/api/sage/ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: userId,
                  question: question,
                  language: selectedLanguage,
                  is_followup: conversationCount > 0,
                  session_id: `session_${userId}_${new Date().toISOString().split('T')[0]}`
                })
              });
              const retryData = await retryRes.json();
              if (retryData.success) {
                setSageAnswer({
                  text: retryData.answer,
                  allTexts: retryData.answers_all_languages || { [selectedLanguage]: retryData.answer },
                  audio: retryData.audio_base64,
                  audioAllLanguages: retryData.audio_all_languages || { [selectedLanguage]: retryData.audio_base64 }
                });
                setSageCredits(retryData.credits_remaining);
                setConversationCount(prev => prev + 1);
                setSageQuestion('');
                persistSageCredits(retryData.credits_remaining);
                const audioToPlay = retryData.audio_all_languages?.[selectedLanguage] || retryData.audio_base64;
                if (audioToPlay) playAnswerAudio(audioToPlay);
                return;
              }
            }
          } catch (syncErr) {
            console.error('Auto-sync retry failed:', syncErr);
          }
        }
        toast.error(data.message || 'Not enough credits');
        setShowCreditsModal(true);
        return;
      }
      
      if (data.success) {
        const allTexts = data.answers_all_languages || { [selectedLanguage]: data.answer };
        setSageAnswer({
          text: data.answer,
          allTexts: allTexts,
          audio: null,
          audioAllLanguages: {}
        });
        setSageCredits(data.credits_remaining);
        setConversationCount(prev => prev + 1);
        setSageQuestion('');
        persistSageCredits(data.credits_remaining);
        
        // Reset audio data ref for new question (and clear persisted cache — new question)
        audioDataRef.current = {};
        try { localStorage.removeItem(SAGE_CACHE_KEY); } catch {}
        
        // Fetch TTS audio for ALL 3 languages concurrently (like Past Story / Future Reels)
        setTtsLoading(true);
        const ttsLangs = Object.keys(allTexts);
        
        // Timeout wrapper — if one language hangs, don't block the others
        const fetchWithTimeout = (url, options, timeoutMs = 25000) => {
          return Promise.race([
            fetch(url, options),
            new Promise((_, reject) => setTimeout(() => reject(new Error('TTS timeout')), timeoutMs))
          ]);
        };
        
        const ttsPromises = ttsLangs.map(lang => 
          fetchWithTimeout(`${BACKEND_URL}/api/sage/tts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: allTexts[lang], language: lang })
          }).then(r => r.json()).then(d => ({ lang, ...d })).catch(() => ({ lang, success: false }))
        );
        
        // Resolve TTS results as they come in — show Listen as soon as first language loads
        ttsPromises.forEach(p => p.then(r => {
          if (r.success && r.audio_base64) {
            audioDataRef.current = { ...audioDataRef.current, [r.lang]: r.audio_base64 };
            setSageAnswer(prev => prev ? ({
              ...prev,
              audioAllLanguages: { ...audioDataRef.current }
            }) : prev);
          }
        }));
        
        Promise.all(ttsPromises).then(() => setTtsLoading(false));
      } else {
        toast.error(data.message || 'Failed to get answer');
      }
    } catch (error) {
      console.error('Sage API Error:', error);
      if (error.name === 'AbortError') {
        toast.error('Sage is taking too long. Please try again.');
      } else {
        toast.error('Sage is busy, please try again in a moment');
      }
    } finally {
      setSageLoading(false);
    }
  };

  // Play audio and switch language
  const playAnswerAudio = (base64Audio) => {
    if (audioRef.current) audioRef.current.pause();
    
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    audioRef.current = audio;
    
    audio.onplay = () => setIsPlayingAnswer(true);
    audio.onended = () => setIsPlayingAnswer(false);
    audio.onerror = (e) => { console.log('Audio error:', e); setIsPlayingAnswer(false); };
    
    // On mobile, play() requires direct user gesture — use play() only when called from tap handler
    const playPromise = audio.play();
    if (playPromise) {
      playPromise.catch((err) => {
        console.log('Auto-play blocked (expected on mobile):', err.message);
        setIsPlayingAnswer(false);
      });
    }
  };
  
  // Switch language and play that audio
  // Fetch TTS for a language on demand (used by Listen button + language switcher)
  const fetchTtsForLang = async (lang) => {
    const textForLang = sageAnswer?.allTexts?.[lang] || sageAnswer?.text;
    if (!textForLang) return null;
    try {
      setTtsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/sage/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textForLang, language: lang })
      });
      const data = await response.json();
      setTtsLoading(false);
      if (data.success && data.audio_base64) {
        audioDataRef.current = { ...audioDataRef.current, [lang]: data.audio_base64 };
        setSageAnswer(prev => prev ? ({
          ...prev,
          audioAllLanguages: { ...audioDataRef.current }
        }) : prev);
        playAnswerAudio(data.audio_base64);
        return data.audio_base64;
      }
    } catch (err) {
      setTtsLoading(false);
      console.error('On-demand TTS failed:', err);
    }
    return null;
  };

  const switchLanguageAndPlay = async (lang) => {
    // Stop current audio when switching languages
    if (audioRef.current) { audioRef.current.pause(); setIsPlayingAnswer(false); }
    setSelectedLanguage(lang);
    
    // Check ref for latest audio data (avoids stale closure)
    const audioForLang = audioDataRef.current[lang] || sageAnswer?.audioAllLanguages?.[lang];
    if (audioForLang) {
      playAnswerAudio(audioForLang);
      return;
    }
    
    // No audio yet - generate on-demand via TTS endpoint
    await fetchTtsForLang(lang);
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported');
      return;
    }
    
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.lang = selectedLanguage === 'hindi' ? 'hi-IN' : 'en-IN';
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  const closeSage = () => {
    setSageExpanded(false);
    // Keep cached answer — only clear question input
    setSageQuestion('');
    if (audioRef.current) audioRef.current.pause();
    setIsPlayingAnswer(false);
  };

  const isDemo = localStorage.getItem('demo_source') === 'emergent';

  return (
    <div 
      className={`bg-gradient-to-b from-[#0a1628] via-[#0d1a30] to-[#0a1628] flex flex-col ${isDemo ? '' : 'overflow-hidden'}`}
      style={{ 
        height: isDemo ? 'auto' : `${initialHeightRef.current}px`,
        minHeight: isDemo ? '100vh' : undefined,
        maxHeight: isDemo ? 'none' : '100svh'
      }}
    >
      {/* Stars Background */}
      <StarsBackground />
      
      {/* Subtle cosmic background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/3 rounded-full blur-[120px]" />
      </div>

      {/* Header - with safe area padding for mobile status bar */}
      <div className="px-3 pb-1 relative z-10" style={{ paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-start justify-between gap-2">
          {/* Spacer - Left (matches credits badge width for centering).
              Width has to mirror the right slot or the title shifts off-center. */}
          <div style={{ width: 76, flexShrink: 0 }} />
          <div className="text-center flex-1 min-w-0" style={{ overflow: 'hidden' }}>
            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              // Smaller on tiny phones so the credits pill (Coins ###) never
              // collides with the title — was overflowing on iPhone 13 Mini and
              // narrow Androids when credits hit 4 digits.
              fontSize: isTinyMobile ? 16 : isMobile ? 19 : 24,
              fontWeight: 500,
              background: 'linear-gradient(180deg, #F3E5AB 0%, #D4AF37 60%, #B8860B 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              // Reduced letter-spacing on phones so the title fits inside the
              // available width between the two 76px slots.
              letterSpacing: isMobile ? '0.08em' : '0.18em',
              textTransform: 'uppercase',
              marginBottom: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.1,
              textShadow: '0 0 18px rgba(212,175,55,0.3)',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
            }}>Future Story Reels</h1>
            {(() => {
              const readyCount = Object.values(audioReadyDomains).filter(Boolean).length;
              const allLoaded = readyCount === 8 || audioAllReady;
              const showLoadingMeta = isUnlocked && futureReelsUnlocked && !allLoaded;
              if (!showLoadingMeta && !allLoaded) return null;
              return (
                <>
                  <p style={{
                    color: allLoaded ? 'rgba(212,175,55,0.8)' : 'rgba(130,130,150,0.6)',
                    fontSize: isMobile ? 10 : 12,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }} data-testid="loading-subtitle">
                    {allLoaded
                      ? 'Tap a planet to listen'
                      : readyCount > 0
                        ? `Preparing ${Math.min(readyCount, 8)} of 8`
                        : reelsGenerating && !reelsReady
                          ? 'Creating your predictions...'
                          : 'Awakening your future...'}
                  </p>
                  {!allLoaded && showLoadingMeta && (
                    <div style={{ maxWidth: 160, margin: '7px auto 0', height: 1.5, borderRadius: 1, background: 'rgba(212,175,55,0.08)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${(readyCount / 8) * 100}%`,
                        background: 'linear-gradient(90deg,#D4AF37,#E8C170)',
                        borderRadius: 1,
                        transition: 'width 0.6s ease',
                      }} data-testid="loading-progress-bar" />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          {/* Credits Badge - Top Right - Clickable to open refill modal.
              Width set to 76px (matches left spacer for centered title) — accommodates
              up to 4-digit credits ("Coins 9999"). zIndex bumped above title so the
              tap target is never blocked by a cosmetic overflow on small screens. */}
          <div style={{ width: 76, flexShrink: 0, zIndex: 20, position: 'relative' }} className="flex justify-end">
            {isUnlocked && (
              <button 
                onClick={() => setShowCreditsModal(true)}
                className="flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/40 px-2.5 py-1 rounded-full hover:bg-amber-500/30 transition-all active:scale-95"
                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                data-testid="sage-credits-badge">
                <Coins className="w-3.5 h-3.5 text-amber-400" />
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(251,191,36,1)' }}>{sageCredits}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unlock Banner - Only when locked.
          Positioned just BELOW the header (title sits at safe-area-top + ~10-30px line-height
          depending on device). Using safe-area-aware top so it never overlaps "FUTURE STORY REELS"
          on iPhones with Dynamic Island/notch (where safe-area-inset-top is ~50-59px).
          - iPhone SE (no notch): safe-area=0, button at ~70px ✅ below title
          - iPhone 13/14/15: safe-area=47px, button at ~117px ✅ clears title
          - iPhone 14 Pro+ (Dynamic Island): safe-area=59px, button at ~129px ✅
          - Android: safe-area=0-24px, button at ~70-94px ✅ */}
      {!isUnlocked && (
        <div
          className="absolute inset-x-0 z-50 flex justify-center px-4"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 70px)' }}
        >
          <button onClick={() => navigate('/payment-plans')}
            className="flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-lg">
            <span style={{ color: '#0a0e14', fontWeight: '600', fontSize: '13px' }}>Unlock All Stories</span>
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 relative" style={{ 
        paddingBottom: '60px',
        height: keyboardOpen ? `${initialHeightRef.current}px` : undefined,
        minHeight: keyboardOpen ? `${initialHeightRef.current * 0.6}px` : undefined,
        overflow: keyboardOpen ? 'hidden' : undefined,
        transform: keyboardOpen ? 'none' : undefined,
        willChange: 'auto'
      }}>
        
        {/* Quick Questions - Proper arc layout around the sage */}
        {sageExpanded && isUnlocked && (
          <div className="absolute inset-0 z-30 pointer-events-none overflow-hidden">
            {/* Container for arc positioning - centered on sage */}
            <div 
              className="absolute left-1/2 -translate-x-1/2"
              style={{ 
                top: centerYpx,
                transform: 'translate(-50%, -50%)',
                width: '100%',
                maxWidth: isTinyMobile ? '280px' : isSmallMobile ? '300px' : isLargeDesktop ? '500px' : isDesktop ? '440px' : '340px',
                height: isTinyMobile ? '280px' : isSmallMobile ? '300px' : isLargeDesktop ? '500px' : isDesktop ? '440px' : '340px',
              }}
            >
              {QUICK_QUESTIONS.map((q, idx) => {
                // Calculate arc position - 7 questions in a semi-circle around sage
                const totalQuestions = QUICK_QUESTIONS.length;
                
                // Arc spans from -100 degrees to +100 degrees (narrower arc)
                const startAngle = -100;
                const endAngle = 100;
                const angleSpan = endAngle - startAngle;
                const angleStep = angleSpan / (totalQuestions - 1);
                const angle = startAngle + (idx * angleStep);
                
                // Convert angle to radians (subtract 90 to start from top)
                const radians = (angle - 90) * (Math.PI / 180);
                
                // Smaller radius to keep bubbles inside container
                const radiusPercent = isTinyMobile ? 32 : isSmallMobile ? 34 : isMobile ? 36 : 42;
                
                // Calculate x, y positions (centered at 50%, 50%)
                const x = 50 + (radiusPercent * Math.cos(radians));
                const y = 50 + (radiusPercent * Math.sin(radians) * 0.9); // slightly compressed vertically
                
                const fontSize = isTinyMobile ? '8px' : isSmallMobile ? '8px' : isMobile ? '9px' : isLargeDesktop ? '14px' : isDesktop ? '13px' : '11px';
                const iconSize = isTinyMobile ? '8px' : isSmallMobile ? '9px' : isMobile ? '10px' : isLargeDesktop ? '15px' : isDesktop ? '14px' : '12px';
                const padding = isTinyMobile ? 'px-1.5 py-0.5' : isSmallMobile ? 'px-2 py-0.5' : (isDesktop || isLargeDesktop) ? 'px-3 py-1.5' : 'px-2 py-1';
                
                return (
                  <button
                    key={q.id}
                    onClick={() => handleQuickQuestion(q.text)}
                    disabled={sageLoading}
                    className="absolute pointer-events-auto group transition-all duration-300 hover:scale-110 active:scale-95"
                    style={{ 
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: 'translate(-50%, -50%)',
                      animation: `scaleIn 0.4s ease-out ${idx * 0.06}s both`,
                    }}
                    data-testid={`quick-q-${idx}`}
                  >
                    <div className={`rounded-full backdrop-blur-md whitespace-nowrap ${padding}`}
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(15,20,35,0.95) 0%, rgba(8,12,22,0.98) 100%)',
                        border: '1px solid rgba(212,175,55,0.35)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5), 0 0 15px rgba(212,175,55,0.2)',
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        <span style={{ color: 'rgba(251,191,36,0.95)', fontSize: iconSize }}>{q.icon}</span>
                        <span style={{ fontSize, color: 'rgba(255,255,255,0.95)', fontWeight: '500' }} className="group-hover:text-white">
                          {q.text}
                        </span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Removed decorative lines for cleaner look */}

        {/* Center Sun (Sage) - with Solar Corona Effect */}
        <div className="absolute z-20"
          style={{ 
            left: `${centerX}%`, 
            top: centerYpx,
            transform: 'translate(-50%, -50%)',
            animation: 'centerFloat 4s ease-in-out infinite',
          }}>
          
          {/* Sun corona - subtle warm glow */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`rounded-full absolute transition-all duration-700 ${
              sageExpanded 
                ? isTinyMobile ? 'w-28 h-28' : isSmallMobile ? 'w-32 h-32' : isMobile ? 'w-36 h-36' : isLargeDesktop ? 'w-72 h-72' : isDesktop ? 'w-64 h-64' : 'w-44 h-44'
                : isTinyMobile ? 'w-20 h-20' : isSmallMobile ? 'w-24 h-24' : isMobile ? 'w-28 h-28' : isLargeDesktop ? 'w-56 h-56' : isDesktop ? 'w-52 h-52' : 'w-36 h-36'
            }`} style={{
              background: 'radial-gradient(circle, rgba(237,193,97,0.12) 0%, rgba(237,193,97,0.04) 40%, transparent 65%)',
              animation: 'solarPulse 3.5s ease-in-out infinite',
            }} />
          </div>
          
          {/* Sage Avatar */}
          <button 
            onClick={handleSageClick}
            className="relative group cursor-pointer focus:outline-none"
            data-testid="sage-avatar-button"
          >
            {/* Soft ambient glow behind sage */}
            <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
              sageExpanded ? 'scale-[2]' : isUnlocked ? 'scale-[1.6] group-hover:scale-[1.8]' : 'scale-[1.3]'
            }`} style={{
              background: isUnlocked 
                ? 'radial-gradient(circle, rgba(237,193,97,0.2) 0%, rgba(237,193,97,0.06) 40%, transparent 65%)'
                : 'radial-gradient(circle, rgba(150,150,150,0.1) 0%, transparent 55%)',
              filter: 'blur(10px)',
            }} />
            
            {/* Sage Image - clean floating with golden drop-shadow */}
            {(() => {
              // Explicit pixel dimensions — Tailwind has no h-22/h-26/h-34 etc so inline style is the reliable option
              const sz = sageExpanded
                ? (isTinyMobile ? { w: 56, h: 72 } : isSmallMobile ? { w: 64, h: 82 } : isLargeDesktop ? { w: 128, h: 176 } : isDesktop ? { w: 112, h: 160 } : { w: 80, h: 104 })
                : (isTinyMobile ? { w: 44, h: 56 } : isSmallMobile ? { w: 52, h: 68 } : isLargeDesktop ? { w: 112, h: 160 } : isDesktop ? { w: 96, h: 132 } : { w: 64, h: 84 });
              // Circular progress ring around Sage — shows overall Future Reels generation progress
              const DOMAIN_KEYS = ['health','career','wealth','marriage','family','education','travel','spiritual'];
              const readyCountRing = DOMAIN_KEYS.filter(k => audioReadyDomains[k] === true).length;
              const partialSum = DOMAIN_KEYS.reduce((s, k) => s + (audioReadyDomains[k] ? 100 : Math.min(100, domainProgress[k] || 0)), 0);
              const overallPct = Math.max(0, Math.min(100, Math.round(partialSum / 8)));
              const showRing = isUnlocked && futureReelsUnlocked && readyCountRing < 8 && (generationActive || reelsGenerating || overallPct > 0);
              const ringSize = Math.max(sz.w, sz.h) + 22;
              const ringR = ringSize / 2 - 4;
              const ringC = 2 * Math.PI * ringR;
              return (
                <div className="relative inline-block">
                  {showRing && (
                    <svg
                      width={ringSize} height={ringSize}
                      style={{
                        position: 'absolute', left: '50%', top: '50%',
                        transform: 'translate(-50%, -50%) rotate(-90deg)',
                        pointerEvents: 'none',
                      }}
                      data-testid="sage-progress-ring"
                    >
                      <circle cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="2" />
                      <circle
                        cx={ringSize/2} cy={ringSize/2} r={ringR} fill="none"
                        stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"
                        strokeDasharray={`${ringC}`}
                        strokeDashoffset={`${ringC * (1 - overallPct / 100)}`}
                        style={{ transition: 'stroke-dashoffset 0.6s ease', filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.55))' }}
                      />
                    </svg>
                  )}
                  <img 
                    src="/sage-final.png" 
                    alt="Sage" 
                    className={`relative object-contain transition-all duration-300 ${isUnlocked ? '' : 'grayscale opacity-50'} ${!sageExpanded && isUnlocked ? 'group-hover:scale-110' : ''}`}
                    style={{ 
                      width: sz.w, height: sz.h,
                      filter: isUnlocked 
                        ? 'drop-shadow(0 0 15px rgba(237,193,97,0.4)) drop-shadow(0 0 30px rgba(237,193,97,0.15))' 
                        : 'grayscale(1)',
                    }} 
                  />
                </div>
              );
            })()}
            
            {/* "Ask Me" Badge */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full transition-all ${
                sageExpanded
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/50'
                  : isUnlocked 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/40 group-hover:shadow-amber-500/60 group-hover:scale-105' 
                    : 'bg-white/10 text-white/40'
              }`} style={{ fontSize: isTinyMobile ? '9px' : '10px', fontWeight: '600' }}>
                {sageExpanded ? 'Ask Your Question' : isUnlocked ? 'Tap to Ask' : 'Locked'}
              </div>
            </div>
          </button>
        </div>

        {/* ================ 3D SOLAR SYSTEM — Safe-zone layout, no overlap ================ */}
        {(() => {
          const tilt = 50;
          const cosT = 0.6428;
          const tiny = isTinyMobile;
          const mob  = isSmallMobile || isMobile;
          const tab  = isTablet;
          
          // Safe-zone reservations (pixels)
          const headerH = 90;                          // title + progress bar + safe-area (compact)
          const navH = 70;                             // bottom nav bar
          const comingSoonH = tiny ? 55 : mob ? 60 : 65; // compact Kundli Milan + Corporate Jyotish cards (single-row tags)
          const phraseH = 36;                          // rotating phrase + its gap
          const safetyGap = tiny ? 6 : 10;             // extra breathing room so labels never kiss cards
          // Android system nav bar (3-button/gesture) can reserve ~24-48px at bottom
          const androidSystemBar = mob || tiny ? 24 : 0;
          const topReserved = headerH + safetyGap;
          // Shift system upward by biasing more bottom-reserved space (visual weight at center)
          const bottomReserved = navH + comingSoonH + phraseH + safetyGap + androidSystemBar;
          
          const availH = Math.max(200, screenSize.height - topReserved - bottomReserved);
          const availW = screenSize.width - 12;        // small horizontal gutter
          
          // Responsive planet size — BIGGER on all viewports for prominence
          const planetSzBase = tiny ? 34 : mob ? 44 : tab ? 58 : isLargeDesktop ? 72 : 62;
          const planetSz = availH < 360 ? Math.max(28, planetSzBase - 8) : planetSzBase;
          const labelAbove = 34, labelBelow = 34;      // label pill can sit either side of planet
          
          const preferOuter = tiny ? 150 : mob ? 195 : tab ? 290 : isLargeDesktop ? 460 : 370;
          const sageClearance = tiny ? 54 : mob ? 64 : tab ? 88 : isLargeDesktop ? 112 : 98;
          
          // Derive maximum allowed outerR from both axes, so orbits NEVER spill into other zones
          const outerR_H = Math.max(80, (availH - planetSz - labelAbove - labelBelow) / (2 * cosT));
          const outerR_W = Math.max(80, (availW - planetSz) / 2 - 6);
          const outerR = Math.min(preferOuter, outerR_H, outerR_W);
          
          // innerR must clear Sage AND keep the label pill (which hangs ~45px below the planet
          // center) from overlapping the Sage on the innermost orbit.
          const minInner = Math.min(sageClearance + planetSz / 2 + 46, outerR * 0.6);
          const innerR = Math.max(outerR * 0.38, minInner);
          const stepR = (outerR - innerR) / 7;
          const systemSz = outerR * 2 + planetSz + 80;
          
          // Pixel-exact center — slightly above mid of safe zone (visually balanced w/ sage)
          const centerYpx = topReserved + availH * 0.46;
          
          return (
            <>
              <div
                className={`absolute left-1/2 transition-all duration-500 ${sageExpanded ? 'opacity-15 pointer-events-none' : 'opacity-100'}`}
                style={{
                  top: centerYpx,
                  transform: 'translate(-50%, -50%)',
                  width: systemSz, height: systemSz,
                  perspective: '1400px', perspectiveOrigin: '50% 50%',
                  zIndex: 10,
                }}
              >
                <div style={{
                  position: 'absolute', inset: 0,
                  transformStyle: 'preserve-3d',
                  transform: `rotateX(${tilt}deg)`,
                }}>
                  {/* Render outer orbits first so innermost planets sit on top */}
                  {domains.slice().reverse().map((d) => {
                    const i = domains.indexOf(d);
                    const radius = innerR + stepR * i;
                    const isReady = isDomainReady(d.domainKey);
                    const isLoadingDomain = isDomainLoading(d.domainKey);
                    const pct = getDomainProgressPercent(d.domainKey);
                    const planetLocked = !isUnlocked || !futureReelsUnlocked;
                    const active = isReady || isLoadingDomain;
                    // Map HexStory domain → PlanetBody-compatible shape
                    const pb = { key: d.domainKey, name: d.name, type: d.type, color: d.color, grad: d.grad };

                    return (
                      <React.Fragment key={d.id}>
                        {/* Orbit ring — elliptical via tilted plane */}
                        <div style={{
                          position: 'absolute', left: '50%', top: '50%',
                          width: radius * 2, height: radius * 2,
                          marginLeft: -radius, marginTop: -radius,
                          borderRadius: '50%',
                          border: `1px solid ${active ? d.color + '55' : 'rgba(212,175,55,0.11)'}`,
                          boxShadow: active
                            ? `0 0 28px ${d.color}22, inset 0 0 36px ${d.color}12`
                            : 'inset 0 0 22px rgba(212,175,55,0.04)',
                          transition: 'border-color 0.8s ease, box-shadow 0.8s ease',
                          pointerEvents: 'none',
                        }} />
                        {/* Static rotator — planet parked at startDeg (angle) */}
                        <div style={{
                          position: 'absolute', left: '50%', top: '50%',
                          width: radius * 2, height: radius * 2,
                          marginLeft: -radius, marginTop: -radius,
                          transform: `rotate(${d.angle}deg)`,
                          transformStyle: 'preserve-3d',
                          pointerEvents: 'none',
                        }}>
                          {/* Planet-holder at 12 o'clock — taller vertically so labels fit
                              either above or below the planet depending on orbital position. */}
                          <div style={{
                            position: 'absolute', left: '50%', top: 0,
                            width: Math.max(220, planetSz * 4), height: planetSz + 100,
                            marginLeft: -Math.max(220, planetSz * 4) / 2, marginTop: -(planetSz + 100) / 2,
                            transform: `rotate(${-d.angle}deg)`,
                            transformStyle: 'preserve-3d',
                            overflow: 'visible',
                          }}>
                            <div style={{
                              transform: `rotateX(${-tilt}deg)`,
                              width: '100%', height: '100%', position: 'relative',
                              overflow: 'visible',
                              transformStyle: 'preserve-3d',
                            }}>
                              {/* Visual sphere (non-interactive — clicks handled by 2D overlay) */}
                              <div style={{
                                position: 'absolute',
                                left: '50%', top: (planetSz + 100) / 2,
                                transform: 'translate(-50%, -50%) translateZ(1px)',
                                width: planetSz, height: planetSz,
                                pointerEvents: 'none',
                              }}>
                                {/* Outer glow */}
                                {active && (
                                  <div style={{
                                    position: 'absolute', inset: -planetSz * 0.4,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${d.color}55 0%, ${d.color}18 35%, transparent 70%)`,
                                    filter: 'blur(6px)', opacity: isReady ? 1 : 0.6,
                                  }} />
                                )}
                                {/* Loading progress ring */}
                                {isLoadingDomain && (
                                  <svg style={{ position: 'absolute', inset: -4, width: planetSz + 8, height: planetSz + 8, transform: 'rotate(-90deg)' }}>
                                    <circle cx={(planetSz + 8) / 2} cy={(planetSz + 8) / 2} r={planetSz / 2 + 1} fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="1.5" />
                                    <circle cx={(planetSz + 8) / 2} cy={(planetSz + 8) / 2} r={planetSz / 2 + 1} fill="none" stroke={d.color} strokeWidth="2"
                                      strokeDasharray={`${Math.PI * (planetSz + 2)}`}
                                      strokeDashoffset={`${Math.PI * (planetSz + 2) * (1 - pct / 100)}`}
                                      strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                                  </svg>
                                )}
                                {/* Sphere */}
                                <div style={{
                                  width: planetSz, height: planetSz, position: 'relative',
                                  opacity: isReady ? 1 : isLoadingDomain ? 0.85 : planetLocked ? 0.35 : 0.55,
                                  filter: (!isReady && !isLoadingDomain) ? 'grayscale(0.5) brightness(0.6)' : 'none',
                                  transition: 'opacity 0.8s ease, filter 0.8s ease',
                                  borderRadius: '50%',
                                  boxShadow: isReady ? `0 0 ${planetSz * 0.55}px ${d.color}60, 0 0 ${planetSz * 0.18}px ${d.color}90` : 'none',
                                }}>
                                  <PlanetBody d={pb} size={planetSz} active={active} />
                                </div>
                                {/* Icon overlay */}
                                {isReady ? (
                                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                                    <div style={{
                                      width: planetSz * 0.5, height: planetSz * 0.5, borderRadius: '50%',
                                      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      boxShadow: `0 0 8px ${d.color}60`,
                                    }}>
                                      <Play style={{ width: planetSz * 0.22, height: planetSz * 0.22, color: '#fff', marginLeft: 1.5 }} fill="currentColor" strokeWidth={0} />
                                    </div>
                                  </div>
                                ) : planetLocked ? (
                                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                                    <Lock style={{ width: planetSz * 0.3, height: planetSz * 0.3, color: 'rgba(255,255,255,0.55)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))' }} />
                                  </div>
                                ) : null}
                                {/* Loading pulse */}
                                {isLoadingDomain && (
                                  <div style={{
                                    position: 'absolute', inset: -1, borderRadius: '50%',
                                    border: `1.5px solid ${d.color}50`,
                                    animation: 'lpulse 2s ease-in-out infinite',
                                  }} />
                                )}
                              </div>

                              {/* Label pill removed from 3D chain — rendered in 2D overlay below
                                  so it can be positioned radially outward from Sun without overlap. */}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* ===== 2D label overlay — positioned RADIALLY OUTWARD from Sun.
                   Lives outside the 3D transform so labels never tilt/overlap/hide behind planets. ===== */}
              <div style={{
                position: 'absolute', left: '50%',
                top: centerYpx,
                transform: 'translate(-50%, -50%)',
                width: 0, height: 0,
                zIndex: 20,
                pointerEvents: 'none',
              }}>
                {domains.map((d, i) => {
                  const radius = innerR + stepR * i;
                  const theta = d.angle * Math.PI / 180;
                  // Label sits radially beyond the planet — distance from center scaled up
                  const labelR = radius + planetSz * 0.75 + 14;
                  const rawLx = labelR * Math.sin(theta);
                  const rawLy = -labelR * Math.cos(theta) * cosT;
                  // Clamp label X so it never overflows viewport (labels at ±90° angles would
                  // otherwise get cut by the screen edge on narrow mobile widths)
                  // Char width estimates are upper-bound (include padding + border) so labels
                  // always leave a safe gap from the screen edge.
                  const charW = tiny ? 6 : mob ? 6.5 : 7;
                  const estLabelHalfW = (d.name.length * charW) / 2 + 16;
                  const maxX = screenSize.width / 2 - estLabelHalfW - 6;
                  const llx = Math.max(-maxX, Math.min(maxX, rawLx));
                  const lly = rawLy;
                  const isReady = isDomainReady(d.domainKey);
                  const isLoadingDomain = isDomainLoading(d.domainKey);
                  const pct = getDomainProgressPercent(d.domainKey);
                  return (
                    <div
                      key={`label-${d.id}`}
                      style={{
                        position: 'absolute',
                        left: llx, top: lly,
                        transform: 'translate(-50%, -50%)',
                        padding: isReady ? '3px 9px' : '2px 8px',
                        borderRadius: 12,
                        background: isReady
                          ? `linear-gradient(135deg, ${d.color}28 0%, rgba(8,8,20,0.94) 72%)`
                          : 'rgba(8,8,20,0.82)',
                        border: isReady ? `1px solid ${d.color}65` : '1px solid rgba(255,255,255,0.08)',
                        backdropFilter: 'blur(5px)',
                        WebkitBackdropFilter: 'blur(5px)',
                        color: isReady ? '#F3E5AB' : isLoadingDomain ? 'rgba(243,229,171,0.75)' : 'rgba(180,180,200,0.7)',
                        fontSize: tiny ? 9 : mob ? 10 : 11,
                        textAlign: 'center',
                        fontWeight: isReady ? 700 : 500,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        textShadow: isReady ? `0 0 10px ${d.color}60` : 'none',
                        boxShadow: isReady ? `0 0 14px ${d.color}35, 0 2px 6px rgba(0,0,0,0.6)` : '0 2px 5px rgba(0,0,0,0.45)',
                        pointerEvents: 'none',
                        lineHeight: 1.2,
                      }}
                      data-testid={`planet-label-${d.domainKey}`}
                    >
                      {d.name}
                    </div>
                  );
                })}
              </div>

              {/* ===== 2D invisible click overlay — always on top, mobile-reliable ===== */}
              {!sageExpanded && (
                <div style={{
                  position: 'absolute', left: '50%',
                  top: centerYpx,
                  transform: 'translate(-50%, -50%)',
                  width: 0, height: 0,
                  zIndex: 25,
                }}>
                  {domains.map((d, i) => {
                    const radius = innerR + stepR * i;
                    const theta = d.angle * Math.PI / 180;
                    const lx = radius * Math.sin(theta);
                    const ly = -radius * Math.cos(theta) * cosT;
                    const hit = planetSz * 1.6;
                    return (
                      <button
                        key={`hit-${d.id}`}
                        data-testid={`planet-hit-${d.domainKey}`}
                        aria-label={`Open ${d.name} future story`}
                        onClick={() => handleDomainPlay(d)}
                        style={{
                          position: 'absolute',
                          left: lx, top: ly,
                          width: hit, height: hit,
                          marginLeft: -hit / 2, marginTop: -hit / 2,
                          border: 'none', background: 'transparent', padding: 0,
                          cursor: 'pointer',
                          borderRadius: '50%',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}

        {/* Rotating mystical phrases during loading — keeps users engaged like Past Story */}
        {(() => {
          const readyCount = Object.values(audioReadyDomains).filter(Boolean).length;
          const allLoaded = readyCount === 8 || audioAllReady;
          const showPhrase = isUnlocked && futureReelsUnlocked && !allLoaded && !sageExpanded;
          if (!showPhrase) return null;
          return (
            <div
              className="absolute left-0 right-0 z-10 px-4 flex justify-center"
              style={{ bottom: isLargeDesktop ? '150px' : isDesktop ? '150px' : '140px' }}
              data-testid="loading-phrase"
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={phraseIdx}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: isMobile ? 12 : 14,
                    fontStyle: 'italic',
                    color: 'rgba(243,229,171,0.5)',
                    textAlign: 'center',
                    maxWidth: 280,
                    letterSpacing: '0.02em',
                  }}
                >
                  {LOADING_PHRASES[phraseIdx]}
                </motion.p>
              </AnimatePresence>
            </div>
          );
        })()}

        {/* Coming Soon Features - side by side, above bottom navbar (~70px) */}
        {!sageExpanded && (
          <div 
            className="absolute left-0 right-0 z-10 px-4"
            style={{ 
              // BottomNav (~70px) + safe-area-inset-bottom (iPad/iOS home bar).
              // Use full safe-area calc on every device — desktop browsers ignore env() so it's harmless.
              bottom: isLargeDesktop
                ? 'calc(96px + env(safe-area-inset-bottom, 0px))'
                : isDesktop
                ? 'calc(118px + env(safe-area-inset-bottom, 0px))' /* iPad / large tablet */
                : 'calc(110px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className={`flex gap-2 mx-auto ${isLargeDesktop ? 'max-w-lg' : 'max-w-md'}`} data-testid="hex-coming-soon-row">
              {COMING_SOON_FEATURES.map(feature => (
                <ComingSoonCard 
                  key={feature.id}
                  feature={feature} 
                  isTinyMobile={isTinyMobile}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sage Input & Answer Panel - Fixed above nav bar */}
      {/* When keyboard is open, position above keyboard instead of above nav */}
      {sageExpanded && isUnlocked && (
        <div 
          className="fixed left-0 right-0 z-40 px-3" 
          style={{ 
            bottom: keyboardOpen ? '8px' : '90px',
            transition: 'bottom 0.15s ease-out'
          }} 
          data-testid="sage-inline-panel"
        >
          <div className={`${isLargeDesktop ? 'max-w-lg' : 'max-w-md'} mx-auto bg-[#0a1628]/95 backdrop-blur-xl rounded-2xl border border-amber-500/25 p-4 shadow-2xl`}
            style={{ animation: 'fadeInUp 0.3s ease-out' }}>
            
            {/* Answer Display with Language Switcher */}
            {sageAnswer && sageExpanded && (
              <div className="mb-3" data-testid="sage-answer">
                {/* Pull-down handle — above the answer, like a bottom sheet indicator */}
                <button
                  onClick={() => { setSageExpanded(false); if (audioRef.current) { audioRef.current.pause(); setIsPlayingAnswer(false); } }}
                  className="w-full flex flex-col items-center py-1 mb-2 group cursor-pointer"
                  data-testid="sage-answer-close"
                >
                  <div className="w-10 h-1 rounded-full bg-white/20 group-hover:bg-white/40 group-active:bg-amber-400/50 transition-colors" />
                  <ChevronDown className="w-4 h-4 text-white/25 group-hover:text-white/50 mt-0.5 transition-colors" />
                </button>
                
                <div className="p-3 bg-white/5 rounded-xl border border-white/10">
                  <div className="max-h-36 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', lineHeight: '1.6' }}>
                      {sageAnswer.allTexts?.[selectedLanguage] || sageAnswer.text}
                    </p>
                  </div>

                {/* Audio play bar — always visible when an answer exists.
                    If audio isn't ready yet for the selected language, tapping will
                    fetch & cache it on demand. */}
                {(() => {
                  const hasCurrentAudio = !!(audioDataRef.current[selectedLanguage] || sageAnswer.audioAllLanguages?.[selectedLanguage]);
                  const isLoadingThisLang = ttsLoading && !hasCurrentAudio;
                  return (
                    <button
                      onClick={async () => {
                        if (isPlayingAnswer) {
                          audioRef.current?.pause();
                          setIsPlayingAnswer(false);
                          return;
                        }
                        const cached = audioDataRef.current[selectedLanguage] || sageAnswer.audioAllLanguages?.[selectedLanguage];
                        if (cached) {
                          playAnswerAudio(cached);
                        } else {
                          // Fetch TTS for this language on demand
                          await fetchTtsForLang(selectedLanguage);
                        }
                      }}
                      disabled={isLoadingThisLang}
                      className={`w-full mt-2 flex items-center justify-center gap-2 py-2 rounded-lg transition-all ${
                        isLoadingThisLang ? 'bg-white/5 text-white/30' :
                        isPlayingAnswer ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                        hasCurrentAudio ? 'bg-amber-500/10 text-amber-300/80 border border-amber-500/20 active:bg-amber-500/25' :
                        'bg-amber-500/5 text-amber-300/70 border border-amber-500/15 active:bg-amber-500/20'
                      }`}
                      data-testid="play-answer-audio"
                    >
                      {isLoadingThisLang ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /><span style={{ fontSize: '12px' }}>Loading voice...</span></>
                      ) : isPlayingAnswer ? (
                        <><VolumeX className="w-4 h-4" /><span style={{ fontSize: '12px' }}>Pause</span></>
                      ) : hasCurrentAudio ? (
                        <><Volume2 className="w-4 h-4" /><span style={{ fontSize: '12px' }}>Listen</span></>
                      ) : (
                        <><Volume2 className="w-4 h-4" /><span style={{ fontSize: '12px' }}>Tap to hear voice</span></>
                      )}
                    </button>
                  );
                })()}
                
                {/* Language Switcher - always show for 3 language toggle */}
                <div className="flex justify-center gap-2 mt-2 pt-2 border-t border-white/5">
                    {['hinglish', 'hindi', 'english'].map((lang) => {
                      const hasContent = !!(sageAnswer.allTexts?.[lang] || sageAnswer.audioAllLanguages?.[lang]);
                      return (
                        <button
                          key={lang}
                          onClick={() => switchLanguageAndPlay(lang)}
                          disabled={!hasContent}
                          className={`px-3 py-1.5 rounded-full transition-all ${
                            selectedLanguage === lang
                              ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                              : hasContent
                                ? 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                                : 'bg-white/5 text-white/20 border border-white/5 cursor-not-allowed'
                          }`}
                          style={{ fontSize: '11px', fontWeight: '500' }}
                          data-testid={`lang-switch-${lang}`}
                        >
                          {lang === 'hinglish' ? 'Hinglish' : lang === 'hindi' ? 'हिंदी' : 'English'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Language Selector - Before asking (to choose initial language) */}
            {!sageAnswer && (
              <div className="flex justify-center gap-2 mb-3">
                {['hinglish', 'hindi', 'english'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`px-3 py-1.5 rounded-full transition-all ${
                      selectedLanguage === lang
                        ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50'
                        : 'bg-white/5 text-white/40 border border-white/10'
                    }`}
                    style={{ fontSize: '12px' }}
                    data-testid={`lang-${lang}`}
                  >
                    {lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </button>
                ))}
              </div>
            )}
            
            {/* Input Row */}
            <div className="flex items-center gap-2 bg-white/5 rounded-xl border border-white/10 p-1.5">
              <input
                ref={inputRef}
                type="text"
                value={sageQuestion}
                onChange={(e) => setSageQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !sageLoading && handleAskSage()}
                placeholder="Type your question..."
                className="flex-1 bg-transparent text-white px-3 py-2 text-sm outline-none placeholder:text-white/30"
                disabled={sageLoading}
                data-testid="sage-question-input"
                style={{ fontSize: '14px', minWidth: 0 }}
              />
              
              {/* Send Button */}
              <button
                onClick={() => handleAskSage()}
                disabled={sageLoading || !sageQuestion.trim()}
                className="p-2.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg disabled:opacity-50 hover:brightness-110 transition-all"
                data-testid="send-question-btn"
                style={{ flexShrink: 0 }}
              >
                {sageLoading ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Send className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
            
            {/* Hint text */}
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: '8px' }}>
              100 credits per question
            </p>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%) translateY(-50%); opacity: 0; }
          to { transform: translateX(-50%) translateY(-50%); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(0%) translateY(-50%); opacity: 0; }
          to { transform: translateX(-50%) translateY(-50%); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeInDown {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes gentleFloat {
          0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
          50% { transform: translate(-50%, -50%) translateY(-5px); }
        }
        @keyframes centerFloat {
          0%, 100% { transform: translate(-50%, -50%) translateY(0px); }
          50% { transform: translate(-50%, -50%) translateY(-4px); }
        }
        @keyframes solarPulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes slowSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes lpulse {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.35); opacity: 0; }
        }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      
      {/* Credits Refill Modal */}
      <CreditsRefillModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        userId={userId}
        currentCredits={sageCredits}
        onCreditsUpdated={(newBalance) => setSageCredits(newBalance)}
      />
    </div>
  );
};

export default HexStory;
